import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AppProvider from 'components/AppProvider';
import React, { useState } from 'react';
import Footer from 'components/pages/common/Footer';
import TopBar from '../../../TopBar';
import FormsTable, { AssignedForm } from './forms_table/FormsTable';
import { logGoogleAnalyticsEvent, GoogleAnalyticsEvent } from 'components/utils/ga';

interface Props {
  username: string;
  logOutUrl: string;
  homeUrl: string;
  formsPath: string;
  snaFormsPath: string;
  assignedForms: Array<AssignedForm>;
  showAssignedForms:boolean;
}

export default function Page(props: Props): React.ReactElement {
  const { formsPath, snaFormsPath, showAssignedForms } = props;
  const [isInvalidFormUrl, setIsInvalidFormUrl] = useState(false);
  const [formLink, setFormLink] = useState('');

  const fullUrlRegex = new RegExp(
    `^(http(s)?://)?${window.location.hostname}/(${formsPath}|${snaFormsPath})/[A-Za-z0-9]{6,}$`,
    'i'
  );
  const shortIdRegex = new RegExp('^[A-Za-z0-9]{6,}$', 'i');

  return (
    <AppProvider>
      <TopBar {...props} />

      <Stack
        className="form-index-page-content page-content"
        maxWidth={{ xs: 546 }}
        spacing={7}
        paddingX={{ xs: 2, md: 0 }}
        paddingTop={2}
      >
        <Stack spacing={2} textAlign="start" marginTop="30%">
          <Typography className="form-index-page-title">
            Paste form identifier to access form
          </Typography>
          <Typography className="form-index-page-body">
            Please copy and paste the form identifier below or scan the
            form&apos;s QR code to access the form.
          </Typography>
        </Stack>

        <Stack spacing={4} alignItems="center">
          <FormControl error={isInvalidFormUrl} fullWidth>
            <OutlinedInput
              size="small"
              placeholder={`e.g. "${window.location.protocol}//${window.location.hostname}/${formsPath}/JkrWMv" or "JkrWMv"`}
              onChange={onTextChange}
              className="form-index-page-input"
              onFocus={() => setIsInvalidFormUrl(false)}
              onBlur={() => setIsInvalidFormUrl(
                !fullUrlRegex.test(formLink) && !shortIdRegex.test(formLink)
              )}
            />
            {isInvalidFormUrl && (
              <FormHelperText>
                Please enter a valid form identifier
              </FormHelperText>
            )}
          </FormControl>
          <Button
            variant="contained"
            sx={{ width: '150px' }}
            disabled={isInvalidFormUrl}
            onClick={onClickButton}
          >
            Continue
          </Button>
        </Stack>
        {showAssignedForms && (
          <Stack spacing={4} alignItems="center" sx={{ pb: 6 }}>
            <FormsTable forms={props.assignedForms} />
          </Stack>
        )}
      </Stack>
      <Footer />
    </AppProvider>
  );

  function onTextChange(
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) {
    setFormLink(event.target.value);
  }

  async function onClickButton() {
    if (fullUrlRegex.test(formLink)) {
      let formPath = formLink;

      if (formLink.startsWith('http://') || formLink.startsWith('https://')) {
        formPath = formLink.replace(/http:\/\/|https:\/\//, '');
      }
      formPath = formPath.replace(`${window.location.hostname}/`, '');

      logGoogleAnalyticsEvent(GoogleAnalyticsEvent.FormAccess, {
        event_category: 'form_navigation',
        event_label: 'full_url_input',
        form_id: formPath.split('/').pop()
      });

      window.location.href = formPath;
      return;
    }

    if (shortIdRegex.test(formLink)) {
      const response = await fetch(`/get_form_link?id=${formLink}`);
      if (response.status === 200) {
        const data = await response.json();

        logGoogleAnalyticsEvent(GoogleAnalyticsEvent.FormAccess, {
          event_category: 'form_navigation',
          event_label: 'short_id_input',
          form_id: formLink
        });

        window.location.href = data.form_link;
        return;
      }
    }

    setIsInvalidFormUrl(true);
  }
}
